import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { acceptInviteSchema, type AcceptInviteInput } from '@/lib/form-schemas';

export function AcceptInvitePage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<AcceptInviteInput>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: { name: '', password: '' },
    mode: 'onSubmit',
  });

  const accept = useMutation({
    mutationFn: (v: AcceptInviteInput) =>
      authApi.acceptInvite({
        token,
        password: v.password,
        name: v.name?.trim() || undefined,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/dashboard', { replace: true });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await accept.mutateAsync(values);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not accept invite');
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <UserPlus className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">Accept invitation</h1>
          <p className="text-sm text-mb-text-muted mt-1">
            Set your password to join the organization.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
          <Field label="Your name" hint="Optional" error={form.formState.errors.name?.message}>
            <input type="text" className="mb-input" {...form.register('name')} />
          </Field>
          <Field
            label="Choose a password"
            hint="At least 8 characters"
            required
            error={form.formState.errors.password?.message}
          >
            <input
              type="password"
              autoComplete="new-password"
              className="mb-input"
              {...form.register('password')}
            />
          </Field>
          {submitError ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
              {submitError}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={accept.isPending || form.formState.isSubmitting}
            className="mb-btn-primary w-full"
          >
            {accept.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Join organization
          </button>
        </form>
      </div>
    </div>
  );
}
